/**
 * 65.47, rounds and brass (the owner, Oct 19: "For gun lane, I'm tempted to have it generate bullet / cartridge / empty shell
 * models for the different calibers that we support so far, nice little element but not sure if it causes perf problems as
 * it's a lot of little things"). Presentation only; the sim never reads any of it.
 *
 * - The rounds: each calibre of the cartridge table (65.30: 9 mm, .45, 5.56, 7.62, .30-06, .50), the 40 mm and the LAW's
 *   66 mm rocket, to their real sizes (metres = person units: a person is 1.8), a lathe of a few sides — a live round
 *   (`buildRound`: the case and its bullet, a few dozen triangles) and a spent case.
 * - The brass (`BrassField`): each shot from a gun that ejects throws its case out of the gun's port (EJECT: the D models'
 *   port and its way out), tumbling, bouncing once or twice on the drawn ground, lying a while and sinking away. The perf
 *   design for the owner's worry: two InstancedMeshes for every calibre (the straight cases and the necked: one draw each,
 *   each calibre an instance's scale), a pool of `BRASS_LOOK.cap` cases in a ring (the oldest recycled), none thrown past
 *   `near` people from the camera, every number in preallocated typed arrays (no allocation a shot or a frame), the update a
 *   tight loop over the cases in the air (one ground probe a case, at its throw, for where it will land; a case lying costs
 *   nothing till it sinks).
 */
import * as THREE from 'three';
import { markShared } from './meshBake';
import type { GunKind } from './gunLooks';
import type { GroundProbe } from './personView';

/** The calibres drawn: the cartridge table's, the 40 mm's and the LAW's rocket. */
export type RoundKind = '9mm' | '.45' | '5.56' | '7.62' | '.30-06' | '.50' | '40mm' | 'rocket';
export const ROUND_KINDS: RoundKind[] = ['9mm', '.45', '5.56', '7.62', '.30-06', '.50', '40mm', 'rocket'];
/**
 * Each round's measures (metres): the case's length, its base's and its neck's radius, where its shoulder starts (a share of
 * the case; 1 straight-walled), the whole round's length and its projectile's radius; the case's colour (brass, the 40 mm's
 * aluminium, the rocket's none: it is all motor) and the projectile's.
 */
export const ROUNDS: Record<RoundKind, { case: number; base: number; neck: number; shoulder: number; length: number; nose: number; caseColor: string; noseColor: string }> = {
  '9mm': { case: 0.019, base: 0.00495, neck: 0.00483, shoulder: 1, length: 0.0297, nose: 0.0045, caseColor: '#c49a45', noseColor: '#b06a40' },
  '.45': { case: 0.0228, base: 0.006, neck: 0.006, shoulder: 1, length: 0.0324, nose: 0.00575, caseColor: '#c49a45', noseColor: '#b06a40' },
  '5.56': { case: 0.0447, base: 0.0048, neck: 0.0032, shoulder: 0.8, length: 0.0574, nose: 0.00285, caseColor: '#c49a45', noseColor: '#b06a40' },
  '7.62': { case: 0.0512, base: 0.006, neck: 0.00435, shoulder: 0.78, length: 0.0711, nose: 0.0039, caseColor: '#c49a45', noseColor: '#b06a40' },
  '.30-06': { case: 0.0633, base: 0.006, neck: 0.0043, shoulder: 0.82, length: 0.0848, nose: 0.0039, caseColor: '#c49a45', noseColor: '#b06a40' },
  '.50': { case: 0.099, base: 0.0102, neck: 0.0071, shoulder: 0.78, length: 0.138, nose: 0.0065, caseColor: '#c49a45', noseColor: '#9a9a90' },
  '40mm': { case: 0.046, base: 0.0215, neck: 0.0205, shoulder: 1, length: 0.099, nose: 0.02, caseColor: '#a8a8a0', noseColor: '#4f5a3a' },
  rocket: { case: 0, base: 0.033, neck: 0.033, shoulder: 1, length: 0.5, nose: 0.033, caseColor: '#4f5a3a', noseColor: '#3c4430' },
};
/** A lathe's sides: a round is seen at a few centimetres. */
const SIDES = 6;

/** The spent case's profile (radius, height) at a unit base radius and length, its shoulder at `shoulder`, its neck `neck` of the base. */
function caseProfile(shoulder: number, neck: number): THREE.Vector2[] {
  const p = [new THREE.Vector2(0, 0), new THREE.Vector2(1, 0), new THREE.Vector2(1, shoulder)];
  if (shoulder < 1) p.push(new THREE.Vector2(neck, Math.min(1, shoulder + 0.07)));
  p.push(new THREE.Vector2(neck, 1), new THREE.Vector2(neck * 0.7, 1));   // the mouth, open a little
  return p;
}
const _geo = new Map<string, THREE.BufferGeometry>();
/** A shared lathe (each built once). */
function lathe(key: string, pts: () => THREE.Vector2[], sides = SIDES): THREE.BufferGeometry {
  let g = _geo.get(key);
  if (!g) { g = markShared(new THREE.LatheGeometry(pts(), sides)); _geo.set(key, g); }
  return g;
}
/** The two spent-case shapes every calibre is an instance of (unit base radius and length, along +y): straight-walled and necked. */
export function caseGeometry(necked: boolean): THREE.BufferGeometry {
  return necked ? lathe('case:necked', () => caseProfile(0.78, 0.72)) : lathe('case:straight', () => caseProfile(1, 0.97));
}
const _mat = new Map<string, THREE.MeshLambertMaterial>();
function mat(color: string): THREE.MeshLambertMaterial {
  let m = _mat.get(color);
  if (!m) { m = markShared(new THREE.MeshLambertMaterial({ color, flatShading: true })); _mat.set(color, m); }
  return m;
}

/**
 * A round of `kind` along +z (its base at the origin, its nose forward), metres: the live round (the case and its bullet, two
 * meshes by material), or with `spent` the case alone. Its own geometry (a round in a gun's model is baked with it). `low`:
 * four sides and the plainest profile (~32 triangles: a round in a gun's belt or clip, a few of them at once).
 */
export function buildRound(kind: RoundKind, opts: { spent?: boolean; low?: boolean } = {}): THREE.Group {
  const R = ROUNDS[kind], g = new THREE.Group(), sides = opts.low ? 4 : SIDES;
  const along = (geo: THREE.BufferGeometry) => geo.rotateX(Math.PI / 2);   // (+y → +z)
  if (R.case > 0) {
    const profile = opts.low ? [new THREE.Vector2(0, 0), new THREE.Vector2(1, 0), new THREE.Vector2(R.neck / R.base, 1)] : caseProfile(R.shoulder, R.neck / R.base);
    const shape = profile.map(v => new THREE.Vector2(v.x * R.base, v.y * R.case));
    g.add(new THREE.Mesh(along(new THREE.LatheGeometry(shape, sides)), mat(R.caseColor)));
  }
  if (!opts.spent) {
    // the projectile: from the case's mouth (or the rocket's tail) to its point — an ogive (the 40 mm's and the rocket's rounder)
    const from = R.case, len = R.length - R.case, r = R.nose, blunt = kind === '40mm' || kind === 'rocket';
    const nose = opts.low ? [new THREE.Vector2(r, from), new THREE.Vector2(r * 0.8, from + len * 0.5), new THREE.Vector2(0, R.length)]
      : [new THREE.Vector2(0, from), new THREE.Vector2(r, from), new THREE.Vector2(r, from + len * (blunt ? 0.55 : 0.35)), new THREE.Vector2(r * (blunt ? 0.75 : 0.6), from + len * (blunt ? 0.85 : 0.8)), new THREE.Vector2(0, R.length)];
    g.add(new THREE.Mesh(along(new THREE.LatheGeometry(nose, sides)), mat(R.noseColor)));
  }
  return g;
}

/**
 * Where each gun throws its case (the gun's own space, metres; +x the strong side, +z the muzzle): its port, the way out
 * (a unit's worth, normalised at the throw) and the round. The springfield's case comes out as its bolt is worked
 * (`delay` seconds after the shot: gunBoltShotPose's opening). The tube, the 40 mm: none (the 40 mm's case comes out
 * in the hand at the reload).
 */
export const EJECT: Partial<Record<GunKind, { port: [number, number, number]; out: [number, number, number]; round: RoundKind; delay?: number }>> = {
  rifle: { port: [0.036, 0.018, 0.12], out: [1, 0.45, -0.35], round: '7.62' },
  smg: { port: [0.034, 0.012, 0.06], out: [1, 0.4, -0.25], round: '9mm' },
  lmg: { port: [0.053, -0.024, 0.09], out: [0.55, -0.85, 0.05], round: '7.62' },
  pistol: { port: [0.025, 0.028, 0.06], out: [1, 0.7, -0.3], round: '9mm' },
  m1911: { port: [0.025, 0.028, 0.05], out: [1, 0.7, -0.3], round: '.45' },
  garand: { port: [0.02, 0.045, 0.05], out: [0.3, 1, 0.15], round: '.30-06' },
  springfield: { port: [0.026, 0.035, 0.05], out: [1, 0.55, -0.2], round: '.30-06', delay: 0.5 },
  thompson: { port: [0.032, 0.02, 0.05], out: [1, 0.4, -0.25], round: '.45' },
  bar: { port: [0.034, 0.005, 0.05], out: [1, 0.35, -0.2], round: '.30-06' },
  hmg: { port: [0, -0.07, -0.14], out: [0.15, -1, 0], round: '.50' },
};

/**
 * The brass's dials: the pool (`cap` cases in the air or lying, the oldest recycled), none thrown past `near` person heights
 * from the camera, the throw's speed and its lift (person heights a second), its spread (a share), the tumble (radians a
 * second), the drawn gravity (heights a second² — the jump's felt 10, the walk's sense of weight), each bounce's give and
 * the ground's grip, how long a case lies (s) and how long it takes to sink away.
 */
export const BRASS_LOOK = { cap: 256, near: 25, speed: 1.6, lift: 0.9, spread: 0.25, tumble: [18, 40] as const, gravity: 10, restitution: 0.35, friction: 0.55, bounces: 2, lie: 8, sink: 0.6 };

const SHAPES = 2;   // straight, necked
const _pp = new THREE.Vector3(), _pv = new THREE.Vector3(), _pu = new THREE.Vector3();   // (a delayed throw's own: eject's scratch would clobber them)
const _p = new THREE.Vector3(), _v = new THREE.Vector3(), _u = new THREE.Vector3(), _a = new THREE.Vector3(), _q = new THREE.Quaternion(), _dq = new THREE.Quaternion(), _s = new THREE.Vector3(), _m = new THREE.Matrix4();
/** A hash in [0, 1) of a shot (the bullet's id) and a stream — the case's variety, the same each time. */
const hash = (n: number, k: number) => { let h = Math.imul(n ^ 0x9e3779b9, 0x85ebca6b) ^ Math.imul(k + 1, 0xc2b2ae35); h ^= h >>> 15; h = Math.imul(h, 0x27d4eb2f); h ^= h >>> 13; return (h >>> 0) / 4294967296; };

/**
 * The spent cases in the world: one instanced mesh a shape (two draws for every calibre), a ring of `cap` slots in
 * preallocated arrays. `eject` throws one (or none: past `near`, or the camera not given); `update` moves those in the air
 * against the planet's pull and their ground (probed once, at the throw), and sinks those whose time is up. The meshes sit
 * at a local origin near where the cases are (their instances are float32: a planet-sized coordinate would shake them).
 */
export class BrassField {
  readonly group = new THREE.Group();
  readonly meshes: THREE.InstancedMesh[];
  private readonly cap: number;
  // per slot (the ring's): where, how fast, the turn and the tumble, the ground's radius and the planet's centre, its scale, age, state
  private readonly pos: Float64Array; private readonly vel: Float64Array; private readonly rot: Float32Array; private readonly spin: Float32Array;
  private readonly centre: Float64Array; private readonly ground: Float64Array; private readonly size: Float32Array;
  private readonly age: Float32Array; private readonly gravity: Float32Array; private readonly bounced: Uint8Array;
  /** 0 free, 1 in the air, 2 lying */
  private readonly state: Uint8Array;
  private readonly shape: Uint8Array; private readonly inst: Int32Array;
  /** per shape, its instances' slots (the mesh's first `count` instances are live) */
  private readonly slotOf: Int32Array[];
  private next = 0;
  private readonly origin = new THREE.Vector3();
  private hasOrigin = false;
  /** the springfield's cases waiting for its bolt: when, then the throw's numbers (a small ring) */
  private readonly pending: Float64Array; private readonly pendingKind: Array<RoundKind | null>; private pendingNext = 0;
  /** counted for the probes: cases thrown, and those not (past `near`) */
  thrown = 0; skipped = 0;

  constructor(cap = BRASS_LOOK.cap) {
    this.cap = cap;
    this.pos = new Float64Array(cap * 3); this.vel = new Float64Array(cap * 3); this.rot = new Float32Array(cap * 4); this.spin = new Float32Array(cap * 3);
    this.centre = new Float64Array(cap * 3); this.ground = new Float64Array(cap); this.size = new Float32Array(cap * 2);
    this.age = new Float32Array(cap); this.gravity = new Float32Array(cap); this.bounced = new Uint8Array(cap); this.state = new Uint8Array(cap);
    this.shape = new Uint8Array(cap); this.inst = new Int32Array(cap).fill(-1);
    this.slotOf = Array.from({ length: SHAPES }, () => new Int32Array(cap).fill(-1));
    const brass = mat(ROUNDS['7.62'].caseColor);
    this.meshes = [false, true].map(necked => {
      const m = new THREE.InstancedMesh(caseGeometry(necked), brass, cap);
      m.name = necked ? 'brass:necked' : 'brass:straight';   // (every instanced layer named: tests/instancedNames.test.ts)
      m.count = 0; m.frustumCulled = false;   // (the instances spread round the origin; a few hundred triangles at most)
      m.instanceMatrix.setUsage(THREE.DynamicDrawUsage);
      this.group.add(m);
      return m;
    });
    this.pending = new Float64Array(16 * 12); this.pendingKind = new Array(16).fill(null);
  }

  /** Cases in the world now (in the air or lying). */
  get live(): number { return this.meshes[0].count + this.meshes[1].count; }

  /**
   * Throw a `kind` case from `port` (world) along `out` (world, any length) — its axis along `axis` (the gun's, world) — for a
   * shot seeded `seed`. `up` the ground's normal there (the pull is against it), `unit` world units a person unit (a person
   * is 1.8), `probe` the drawn ground (null: the plane through `feet` across `up`), `camera` where the camera is (none thrown
   * past `near` heights of it). `delay`: thrown that many seconds later (the springfield's bolt; the ground and the pull then
   * the last delayed throw's — the bolt is worked where the shot was).
   */
  eject(kind: RoundKind, port: THREE.Vector3, out: THREE.Vector3, axis: THREE.Vector3, up: THREE.Vector3, unit: number, seed: number, probe: GroundProbe | null, feet: THREE.Vector3, camera: THREE.Vector3 | null, delay = 0): void {
    const R = ROUNDS[kind];
    if (R.case <= 0) return;
    if (!camera || port.distanceToSquared(camera) > (BRASS_LOOK.near * 1.8 * unit) ** 2) { this.skipped++; return; }
    if (delay > 0) {
      const i = this.pendingNext, b = i * 12, P = this.pending;
      P[b] = delay; P[b + 1] = port.x; P[b + 2] = port.y; P[b + 3] = port.z; P[b + 4] = out.x; P[b + 5] = out.y; P[b + 6] = out.z;
      P[b + 7] = axis.x; P[b + 8] = axis.y; P[b + 9] = axis.z; P[b + 10] = unit; P[b + 11] = seed;
      this.pendingKind[i] = kind; this.pendingNext = (i + 1) % 16;
      this.pendingProbe = probe; this.pendingUp.copy(up); this.pendingFeet.copy(feet);
      return;
    }
    this.thrown++; this.unitHint = unit;
    const s = this.next; this.next = (s + 1) % this.cap;
    if (this.state[s] !== 0) this.free(s);   // the oldest, recycled
    const L = BRASS_LOOK, b3 = s * 3, sp = (1 + L.spread * (hash(seed, 0) * 2 - 1)) * L.speed * 1.8 * unit, lift = (1 + L.spread * (hash(seed, 1) * 2 - 1)) * L.lift * 1.8 * unit;
    _v.copy(out).normalize().multiplyScalar(sp).addScaledVector(up, lift);
    this.pos[b3] = port.x; this.pos[b3 + 1] = port.y; this.pos[b3 + 2] = port.z;
    this.vel[b3] = _v.x; this.vel[b3 + 1] = _v.y; this.vel[b3 + 2] = _v.z;
    _q.setFromUnitVectors(_a.set(0, 1, 0), _u.copy(axis).normalize());
    this.rot[s * 4] = _q.x; this.rot[s * 4 + 1] = _q.y; this.rot[s * 4 + 2] = _q.z; this.rot[s * 4 + 3] = _q.w;
    // the tumble: end over end about the way across both the throw and the case (with a little roll of its own)
    _a.copy(out).cross(axis); if (_a.lengthSq() < 1e-12) _a.set(1, 0, 0);
    _a.normalize().multiplyScalar(L.tumble[0] + (L.tumble[1] - L.tumble[0]) * hash(seed, 2)).addScaledVector(_u, 6 * (hash(seed, 3) - 0.5));
    this.spin[b3] = _a.x; this.spin[b3 + 1] = _a.y; this.spin[b3 + 2] = _a.z;
    // the pull and the ground: the planet's centre (the probe's), else a far point below the feet (a plane); the ground's
    // radius from it where the case will land (once, here)
    const c = probe ? probe.centre : null;
    if (c) { this.centre[b3] = c.x; this.centre[b3 + 1] = c.y; this.centre[b3 + 2] = c.z; }
    else { this.centre[b3] = feet.x - up.x * 1e6; this.centre[b3 + 1] = feet.y - up.y * 1e6; this.centre[b3 + 2] = feet.z - up.z * 1e6; }
    const g = L.gravity * 1.8 * unit, h = Math.max(0, _p.copy(port).sub(feet).dot(up)), tFall = (lift + Math.sqrt(lift * lift + 2 * g * h)) / g;
    _p.copy(port).addScaledVector(_v, tFall).addScaledVector(up, -_v.dot(up) * tFall);   // where it comes down, about
    _p.x -= this.centre[b3]; _p.y -= this.centre[b3 + 1]; _p.z -= this.centre[b3 + 2];
    const r = _p.length();
    this.ground[s] = probe ? probe.radiusAt({ x: _p.x / r, y: _p.y / r, z: _p.z / r }) : _u.copy(feet).sub(_a.set(this.centre[b3], this.centre[b3 + 1], this.centre[b3 + 2])).length();
    this.gravity[s] = g;
    this.size[s * 2] = R.base * unit; this.size[s * 2 + 1] = R.case * unit;
    this.age[s] = 0; this.bounced[s] = 0; this.state[s] = 1;
    // its instance in its shape's mesh
    const shape = R.shoulder < 1 ? 1 : 0, mesh = this.meshes[shape], i = mesh.count;
    this.shape[s] = shape; this.inst[s] = i; this.slotOf[shape][i] = s; mesh.count = i + 1;
    if (!this.hasOrigin) { this.origin.set(this.pos[b3], this.pos[b3 + 1], this.pos[b3 + 2]); this.hasOrigin = true; this.group.position.copy(this.origin); }
    this.write(s, 1);
  }
  private pendingProbe: GroundProbe | null = null;
  private readonly pendingUp = new THREE.Vector3(); private readonly pendingFeet = new THREE.Vector3();

  /** Move the cases in the air, age those lying, sink and free those whose time is up; the camera re-centres the meshes when far from their origin. */
  update(dt: number, camera: THREE.Vector3 | null): void {
    const L = BRASS_LOOK;
    // the springfield's cases, their bolt worked
    for (let i = 0; i < 16; i++) {
      const k = this.pendingKind[i]; if (!k) continue;
      const b = i * 12, P = this.pending; P[b] -= dt;
      if (P[b] > 0) continue;
      this.pendingKind[i] = null;
      this.eject(k, _pp.set(P[b + 1], P[b + 2], P[b + 3]), _pv.set(P[b + 4], P[b + 5], P[b + 6]), _pu.set(P[b + 7], P[b + 8], P[b + 9]), this.pendingUp, P[b + 10], P[b + 11], this.pendingProbe, this.pendingFeet, camera);
    }
    if (this.live === 0) return;
    if (camera && camera.distanceToSquared(this.origin) > 2500 * this.unitHint * this.unitHint) this.recentre(camera);
    let moved = false;
    for (let shape = 0; shape < SHAPES; shape++) {
      const mesh = this.meshes[shape], slots = this.slotOf[shape];
      for (let i = 0; i < mesh.count; i++) {
        const s = slots[i];
        this.age[s] += dt;
        if (this.state[s] === 1) { this.fly(s, dt); moved = true; }
        else if (this.age[s] > L.lie) {
          const k = 1 - (this.age[s] - L.lie) / L.sink;
          if (k <= 0) { this.free(s); i--; moved = true; continue; }   // (the last instance moved into this one: look at it again)
          this.write(s, k); moved = true;
        }
      }
    }
    if (moved) for (const m of this.meshes) m.instanceMatrix.needsUpdate = true;
  }
  /** a person unit's world size, for the re-centring distance (set by the last throw) */
  private unitHint = 1;

  /** One case in the air: the pull, the tumble, and the ground — a bounce (its give, the grip), then lying along it. */
  private fly(s: number, dt: number): void {
    const L = BRASS_LOOK, b = s * 3, P = this.pos, V = this.vel;
    _u.set(P[b] - this.centre[b], P[b + 1] - this.centre[b + 1], P[b + 2] - this.centre[b + 2]);
    const r = _u.length(); _u.multiplyScalar(1 / r);   // up
    const g = this.gravity[s] * dt;
    V[b] -= _u.x * g; V[b + 1] -= _u.y * g; V[b + 2] -= _u.z * g;
    P[b] += V[b] * dt; P[b + 1] += V[b + 1] * dt; P[b + 2] += V[b + 2] * dt;
    // the tumble
    const w = _a.set(this.spin[b], this.spin[b + 1], this.spin[b + 2]), ang = w.length() * dt;
    _q.set(this.rot[s * 4], this.rot[s * 4 + 1], this.rot[s * 4 + 2], this.rot[s * 4 + 3]);
    if (ang > 1e-9) { _dq.setFromAxisAngle(w.normalize(), ang); _q.premultiply(_dq); }
    // the ground: a case's half thickness over it
    const rest = this.ground[s] + this.size[s * 2];
    const rNow = Math.sqrt((P[b] - this.centre[b]) ** 2 + (P[b + 1] - this.centre[b + 1]) ** 2 + (P[b + 2] - this.centre[b + 2]) ** 2);
    if (rNow < rest) {
      const push = rest - rNow; P[b] += _u.x * push; P[b + 1] += _u.y * push; P[b + 2] += _u.z * push;
      const vn = V[b] * _u.x + V[b + 1] * _u.y + V[b + 2] * _u.z;
      if (vn < 0) {
        // the bounce: the normal speed turned back by the give, the rest gripped; the tumble checked
        V[b] = (V[b] - _u.x * vn) * L.friction - _u.x * vn * L.restitution;
        V[b + 1] = (V[b + 1] - _u.y * vn) * L.friction - _u.y * vn * L.restitution;
        V[b + 2] = (V[b + 2] - _u.z * vn) * L.friction - _u.z * vn * L.restitution;
        this.spin[b] *= 0.5; this.spin[b + 1] *= 0.5; this.spin[b + 2] *= 0.5;
        if (++this.bounced[s] >= L.bounces || -vn < 0.4 * this.gravity[s] * 0.05) {
          // lying: its axis along the ground (the axis it came down with, the up taken out), still from here on
          _a.set(0, 1, 0).applyQuaternion(_q); _a.addScaledVector(_u, -_a.dot(_u));
          if (_a.lengthSq() < 1e-12) _a.set(1, 0, 0).addScaledVector(_u, -_u.x);
          _q.setFromUnitVectors(_v.set(0, 1, 0), _a.normalize());
          this.state[s] = 2; this.age[s] = 0;
        }
      }
    }
    this.rot[s * 4] = _q.x; this.rot[s * 4 + 1] = _q.y; this.rot[s * 4 + 2] = _q.z; this.rot[s * 4 + 3] = _q.w;
    this.write(s, 1);
  }

  /** Its instance's matrix (`k` its size: 1, less as it sinks). */
  private write(s: number, k: number): void {
    const b = s * 3;
    _p.set(this.pos[b] - this.origin.x, this.pos[b + 1] - this.origin.y, this.pos[b + 2] - this.origin.z);
    _q.set(this.rot[s * 4], this.rot[s * 4 + 1], this.rot[s * 4 + 2], this.rot[s * 4 + 3]);
    const r = this.size[s * 2] * k;
    _s.set(r, this.size[s * 2 + 1] * k, r);
    // (the lathe's base is at its origin: the case's middle on the point, so it turns about its middle)
    _m.compose(_p.addScaledVector(_v.set(0, -0.5 * this.size[s * 2 + 1] * k, 0).applyQuaternion(_q), 1), _q, _s);
    this.meshes[this.shape[s]].setMatrixAt(this.inst[s], _m);
  }

  /** Free slot `s`: its mesh's last instance moved into its place. */
  private free(s: number): void {
    const shape = this.shape[s], mesh = this.meshes[shape], i = this.inst[s], last = mesh.count - 1, slots = this.slotOf[shape];
    if (i !== last) {
      mesh.getMatrixAt(last, _m); mesh.setMatrixAt(i, _m);
      const moved = slots[last]; slots[i] = moved; this.inst[moved] = i;
    }
    slots[last] = -1; mesh.count = last; this.inst[s] = -1; this.state[s] = 0;
    mesh.instanceMatrix.needsUpdate = true;
  }

  /** Move the meshes' origin to `at` and rewrite every instance. */
  private recentre(at: THREE.Vector3): void {
    this.origin.copy(at); this.group.position.copy(at);
    for (let shape = 0; shape < SHAPES; shape++) for (let i = 0; i < this.meshes[shape].count; i++) { const s = this.slotOf[shape][i]; this.write(s, this.state[s] === 2 && this.age[s] > BRASS_LOOK.lie ? Math.max(0, 1 - (this.age[s] - BRASS_LOOK.lie) / BRASS_LOOK.sink) : 1); }
    for (const m of this.meshes) m.instanceMatrix.needsUpdate = true;
  }

  /** Where the live cases are: their centre (world) into `out`, and the farthest's distance from it (0: none). */
  around(out: THREE.Vector3): number {
    out.set(0, 0, 0); let n = 0;
    for (let s = 0; s < this.cap; s++) if (this.state[s] !== 0) { out.x += this.pos[s * 3]; out.y += this.pos[s * 3 + 1]; out.z += this.pos[s * 3 + 2]; n++; }
    if (!n) return 0;
    out.multiplyScalar(1 / n); let r = 0;
    for (let s = 0; s < this.cap; s++) if (this.state[s] !== 0) r = Math.max(r, Math.hypot(this.pos[s * 3] - out.x, this.pos[s * 3 + 1] - out.y, this.pos[s * 3 + 2] - out.z));
    return r;
  }
  /** Each live case: whether it is in the air (1) or lying (2), and where (world). For the probes and the tests. */
  cases(): Array<{ state: number; x: number; y: number; z: number; age: number }> {
    const out: Array<{ state: number; x: number; y: number; z: number; age: number }> = [];
    for (let s = 0; s < this.cap; s++) if (this.state[s] !== 0) out.push({ state: this.state[s], x: this.pos[s * 3], y: this.pos[s * 3 + 1], z: this.pos[s * 3 + 2], age: this.age[s] });
    return out;
  }

  /** Every case gone (a new match). */
  clear(): void {
    for (let s = 0; s < this.cap; s++) if (this.state[s] !== 0) this.free(s);
    this.pendingKind.fill(null); this.hasOrigin = false;
  }
  dispose(): void { for (const m of this.meshes) m.dispose(); }
}

/**
 * Where `gun` (a gun's group, in the scene) of `kind` throws its case, now: its port (world), the way out and the gun's axis
 * (world, unit). False for a gun that throws none.
 */
export function ejectFrom(gun: THREE.Object3D, kind: GunKind, port: THREE.Vector3, out: THREE.Vector3, axis: THREE.Vector3): boolean {
  const e = EJECT[kind];
  if (!e) return false;
  gun.updateWorldMatrix(true, false);
  port.set(e.port[0], e.port[1], e.port[2]).applyMatrix4(gun.matrixWorld);
  out.set(e.out[0], e.out[1], e.out[2]).transformDirection(gun.matrixWorld);
  axis.set(0, 0, 1).transformDirection(gun.matrixWorld);
  return true;
}

/** 65.47: whether the brass is thrown in play — the Lab's *Brass* row, else the page's `?brass=0` to turn it off; on by default (the owner, Oct 19: "Brass by default."). */
let brassOverride: boolean | null = null;
export function setBrassShown(on: boolean | null): void { brassOverride = on; }
export function brassShown(): boolean {
  if (brassOverride !== null) return brassOverride;
  try { return typeof location === 'undefined' || new URLSearchParams(location.search).get('brass') !== '0'; } catch { return true; }
}
